/**
 * FathomVoyage — Bunker Price Monitor
 * Paid API Service — BunkerEx + S&P Global Platts
 *
 * When you're ready to upgrade from scraping, integrate one of these APIs.
 * Both return clean, structured JSON with historical data and change tracking.
 *
 * COST ESTIMATES (April 2026):
 *   BunkerEx API:       ~$500-2,000/mo depending on ports and history depth
 *   S&P Global Platts:  ~$2,000-8,000/mo for bunker price dataset
 *   Ship & Bunker API:  ~$200-500/mo (direct API vs scraping their site)
 */

// ---------------------------------------------------------------------------
// BunkerEx API
// Reference: https://bunkerex.com/api (contact their sales team for access)
// ---------------------------------------------------------------------------

/**
 * BunkerEx integration
 *
 * Auth: Bearer token in Authorization header
 * Base URL: https://api.bunkerex.com/v2
 * Rate limits: 1,000 requests/day on standard tier
 * Data freshness: Updated twice daily (06:00 UTC and 14:00 UTC)
 *
 * Key endpoints:
 *   GET /prices/latest          → Latest prices for all ports
 *   GET /prices/{port}/{fuel}   → Single port/fuel price history
 *   GET /ports                  → Available ports list
 *   GET /alerts                 → Price movement alerts
 */

const BUNKEREX_CONFIG = {
  apiKey:  import.meta.env.VITE_BUNKEREX_API_KEY || '',
  baseUrl: 'https://api.bunkerex.com/v2',
  // Port slug mapping (BunkerEx uses their own port codes)
  portSlugMap: {
    SGP: 'singapore',
    FUJ: 'fujairah',
    RTM: 'rotterdam',
    HOU: 'houston',
    PIR: 'piraeus',
    GIB: 'gibraltar',
    ALG: 'algeciras',
    PAN: 'panama-city',
    DUR: 'durban',
    CPT: 'cape-town',
    CMB: 'colombo',
    JED: 'jeddah',
    MUM: 'mumbai',
    SHA: 'shanghai',
    BUS: 'busan',
    HKG: 'hong-kong',
    KHH: 'kaohsiung',
    BAL: 'balboa',
    LPA: 'las-palmas',
    TPP: 'tanjung-pelepas',
  },
  // BunkerEx fuel codes
  fuelCodeMap: {
    VLSFO: 'VLSFO',
    HSFO:  'HSFO',
    MGO:   'MGO',
    LNG:   'LNG',
  },
};

/**
 * Fetch latest prices from BunkerEx API
 *
 * Expected response shape:
 * {
 *   "data": [
 *     {
 *       "port": "singapore",
 *       "port_code": "SGP",
 *       "prices": {
 *         "VLSFO": { "price": 728.50, "currency": "USD", "unit": "MT",
 *                    "change_24h": -8.00, "change_7d": -15.50,
 *                    "updated_at": "2026-04-04T06:00:00Z" },
 *         "HSFO":  { ... },
 *         "MGO":   { ... },
 *         "LNG":   { ... }
 *       }
 *     }
 *   ],
 *   "meta": { "generated_at": "2026-04-04T07:45:00Z", "coverage": 65 }
 * }
 */
export async function fetchFromBunkerEx() {
  if (!BUNKEREX_CONFIG.apiKey) {
    throw new Error('BUNKEREX_API_KEY not set in environment');
  }

  const response = await fetch(`${BUNKEREX_CONFIG.baseUrl}/prices/latest`, {
    headers: {
      'Authorization': `Bearer ${BUNKEREX_CONFIG.apiKey}`,
      'Content-Type':  'application/json',
      'Accept':        'application/json',
    },
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`BunkerEx API error ${response.status}: ${body}`);
  }

  const json = await response.json();

  // Normalize BunkerEx response → FathomVoyage standard format
  return normalizeBunkerExResponse(json);
}

function normalizeBunkerExResponse(json) {
  const ports = {};

  for (const portData of json.data) {
    // Find our internal port ID by matching port_code
    const portId = portData.port_code;
    if (!portId) continue;

    ports[portId] = {
      // BunkerEx provides 7-day history as an array — map it
      VLSFO: portData.prices.VLSFO ? {
        price:   portData.prices.VLSFO.price,
        change:  portData.prices.VLSFO.change_24h,
        history: portData.prices.VLSFO.history_7d || [],
      } : { price: null, change: null, history: null },

      HSFO: portData.prices.HSFO ? {
        price:   portData.prices.HSFO.price,
        change:  portData.prices.HSFO.change_24h,
        history: portData.prices.HSFO.history_7d || [],
      } : { price: null, change: null, history: null },

      MGO: portData.prices.MGO ? {
        price:   portData.prices.MGO.price,
        change:  portData.prices.MGO.change_24h,
        history: portData.prices.MGO.history_7d || [],
      } : { price: null, change: null, history: null },

      LNG: portData.prices.LNG ? {
        price:   portData.prices.LNG.price,
        change:  portData.prices.LNG.change_24h,
        history: portData.prices.LNG.history_7d || [],
      } : { price: null, change: null, history: null },
    };
  }

  return {
    source:      'bunkerex_api',
    lastUpdated: json.meta.generated_at,
    dataDate:    json.meta.generated_at.split('T')[0],
    ports,
    disclaimer:  null,
  };
}

// ---------------------------------------------------------------------------
// S&P Global Platts API (now S&P Global Commodity Insights)
// Reference: https://developer.spglobal.com/commodity-insights
// ---------------------------------------------------------------------------

/**
 * S&P Global Platts integration
 *
 * Auth: OAuth 2.0 client credentials flow
 * Base URL: https://api.spglobal.com/commodity/v1
 * Rate limits: 10,000 requests/hour on enterprise tier
 * Data freshness: Real-time (intraday updates)
 *
 * Key dataset IDs for bunker prices:
 *   AAFMX00  → Singapore VLSFO 0.5% ($/mt)
 *   AAFMX01  → Rotterdam VLSFO 0.5% ($/mt)
 *   AAFMX02  → Fujairah VLSFO 0.5% ($/mt)
 *   AAFMX03  → Houston VLSFO 0.5% ($/mt)
 *   (Full symbol list available via GET /symbols?category=marine-fuels)
 */

const PLATTS_CONFIG = {
  clientId:     import.meta.env.VITE_PLATTS_CLIENT_ID     || '',
  clientSecret: import.meta.env.VITE_PLATTS_CLIENT_SECRET || '',
  tokenUrl:     'https://api.spglobal.com/auth/token',
  baseUrl:      'https://api.spglobal.com/commodity/v1',
  // Platts symbol → our [portId, fuelType] mapping
  symbolMap: {
    AAFMX00: ['SGP', 'VLSFO'],
    AAFMX01: ['RTM', 'VLSFO'],
    AAFMX02: ['FUJ', 'VLSFO'],
    AAFMX03: ['HOU', 'VLSFO'],
    // Add more symbols as you expand coverage
    // Full list: GET /symbols?category=marine-fuels
  },
};

let plattsAccessToken = null;
let plattsTokenExpiry = null;

async function getPlattsAccessToken() {
  if (plattsAccessToken && plattsTokenExpiry > Date.now()) {
    return plattsAccessToken;
  }

  const response = await fetch(PLATTS_CONFIG.tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type:    'client_credentials',
      client_id:     PLATTS_CONFIG.clientId,
      client_secret: PLATTS_CONFIG.clientSecret,
    }),
  });

  if (!response.ok) {
    throw new Error(`Platts auth failed: ${response.status}`);
  }

  const { access_token, expires_in } = await response.json();
  plattsAccessToken = access_token;
  plattsTokenExpiry = Date.now() + (expires_in - 60) * 1000;  // 60s buffer
  return plattsAccessToken;
}

/**
 * Fetch latest prices from S&P Global Platts
 *
 * Expected response shape:
 * {
 *   "results": [
 *     {
 *       "symbol": "AAFMX00",
 *       "description": "Singapore VLSFO 0.5% ($/mt)",
 *       "value": 728.50,
 *       "unit": "USD/MT",
 *       "timestamp": "2026-04-04T12:30:00Z",
 *       "assessment_date": "2026-04-04",
 *       "change": -8.00,
 *       "change_pct": -1.09
 *     }
 *   ]
 * }
 */
export async function fetchFromPlatts() {
  if (!PLATTS_CONFIG.clientId || !PLATTS_CONFIG.clientSecret) {
    throw new Error('PLATTS_CLIENT_ID / PLATTS_CLIENT_SECRET not set in environment');
  }

  const token   = await getPlattsAccessToken();
  const symbols = Object.keys(PLATTS_CONFIG.symbolMap).join(',');

  const response = await fetch(
    `${PLATTS_CONFIG.baseUrl}/assessments/latest?symbol=${symbols}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept':        'application/json',
      },
    }
  );

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Platts API error ${response.status}: ${body}`);
  }

  const json = await response.json();
  return normalizePlattsResponse(json);
}

function normalizePlattsResponse(json) {
  const ports = {};

  for (const result of json.results) {
    const mapping = PLATTS_CONFIG.symbolMap[result.symbol];
    if (!mapping) continue;

    const [portId, fuelType] = mapping;

    if (!ports[portId]) {
      ports[portId] = {
        VLSFO: { price: null, change: null, history: [] },
        HSFO:  { price: null, change: null, history: [] },
        MGO:   { price: null, change: null, history: [] },
        LNG:   { price: null, change: null, history: [] },
      };
    }

    ports[portId][fuelType] = {
      price:   result.value,
      change:  result.change,
      history: [],  // Request separately via GET /assessments/history?symbol=...&days=7
    };
  }

  return {
    source:      'platts_api',
    lastUpdated: new Date().toISOString(),
    dataDate:    new Date().toISOString().split('T')[0],
    ports,
    disclaimer:  null,
  };
}

// ---------------------------------------------------------------------------
// Ship & Bunker Direct API (cheapest paid option)
// Reference: Contact commercial@shipandbunker.com
// ---------------------------------------------------------------------------

/**
 * Ship & Bunker Direct API
 *
 * Auth: API key in X-API-Key header
 * Base URL: https://api.shipandbunker.com/v1
 * Rate limits: 500 requests/day on standard tier
 * Data freshness: Updated 2x daily
 *
 * This is the most cost-effective paid option for a startup,
 * as you're already scraping their site and they have a low-cost API tier.
 *
 * Expected response shape:
 * {
 *   "prices": {
 *     "SGP": { "VLSFO": 728.5, "HSFO": 458.0, "MGO": 848.0, "LNG": null },
 *     "RTM": { "VLSFO": 694.0, "HSFO": 441.0, "MGO": 812.0, "LNG": 672.0 },
 *     ...
 *   },
 *   "generated_at": "2026-04-04T06:00:00Z"
 * }
 */
export async function fetchFromShipAndBunkerApi() {
  const apiKey = import.meta.env.VITE_SHIP_AND_BUNKER_API_KEY || '';
  if (!apiKey) throw new Error('SHIP_AND_BUNKER_API_KEY not set in environment');

  const response = await fetch('https://api.shipandbunker.com/v1/prices/latest', {
    headers: {
      'X-API-Key': apiKey,
      'Accept':    'application/json',
    },
  });

  if (!response.ok) throw new Error(`Ship&Bunker API error ${response.status}`);

  const json = await response.json();

  // S&B API response is already close to our format — just wrap it
  const ports = {};
  for (const [portId, prices] of Object.entries(json.prices)) {
    ports[portId] = {
      VLSFO: { price: prices.VLSFO, change: null, history: [] },
      HSFO:  { price: prices.HSFO,  change: null, history: [] },
      MGO:   { price: prices.MGO,   change: null, history: [] },
      LNG:   { price: prices.LNG,   change: null, history: [] },
    };
  }

  return {
    source:      'shipandbunker_api',
    lastUpdated: json.generated_at,
    dataDate:    json.generated_at.split('T')[0],
    ports,
    disclaimer:  null,
  };
}
